import React, {useState, useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useAsync} from "react-async";
import ResidentAdminTemplate from "./residentAdminTemplate";
import {useTranslation} from "react-i18next";
import {useStores} from "../../../stores/useStores";
import {ResidentAccountDetail} from "../../../stores/domainStore/pages/models/residentAccountDetail";
import ResidentDetail from "../../../stores/domainStore/pages/models/residentDetail";
import {ToastMessageStatus} from "../../atoms/toastMessage/toastMessage";
import ResidentDetailModal from "../../organisms/residentDetailModal/residentDetailModal";
import ResidentModalDetail from "../../../stores/domainStore/pages/models/residentModalDetail";
import RemoveAccountConfirmModal from "../../organisms/removeAccountConfirmModal/removeAccountConfirmModal";
import {IResidentAdminDomainStore} from "../../../stores/domainStore/pages/residentAdminDomainStore";
import {IResidentAdminUiStore} from "../../../stores/uiStore/pages/residentAdminUiStore";

export interface IResidentAdminPageProps {
  residentAdminDomainStore: IResidentAdminDomainStore;
  residentAdminUiStore: IResidentAdminUiStore;
  namespace?: string;
}

const ResidentAdminPage: React.FC<IResidentAdminPageProps> = ({
  residentAdminDomainStore,
  residentAdminUiStore,
  namespace = "residentadmin"
}) => {
  const {t} = useTranslation(["translation", "residentadmin"]);
  const {
    uiStore: {toastMessageStore}
  } = useStores();

  const [selectedResident, setSelectedResident] = useState<
    ResidentModalDetail | undefined
  >(undefined);
  const [residentAccountResponse, setResidentAccountResponse] = useState<
    ResidentAccountDetail | undefined
  >(undefined);

  const [isResidentDetailModalOpen, setIsResidentDetailModalOpen] =
    useState<boolean>(false);

  const [isResetPasswordSuccessModalOpen, setIsResetPasswordSuccessModalOpen] =
    useState<boolean>(false);

  const [
    isRemoveResidentConfirmationOpen,
    setIsRemoveResidentConfirmationOpen
  ] = useState<boolean>(false);

  const refreshAllData = (persistFilters?: boolean) => {
    // Reload the current page
    residentAdminUiStore.refreshResidentData(persistFilters);
  };

  // Refresh resident list on load - hence the empty dependencies array
  useEffect(() => {
    refreshAllData();
  }, []);

  const createAccount = useAsync({
    deferFn: (args) => residentAdminDomainStore.createAccount(args[0]),
    onResolve: (data: ResidentAccountDetail) => {
      setResidentAccountResponse(data);
      if (data.password) {
        selectedResident?.setPassword(data.password);
      }
      if (!data.error && !data.errorCode) {
        toastMessageStore.displayToastMessage(
          t("residentadmin:labels.createAccountSuccessToast", {
            residentName: selectedResident?.formattedResidentName,
            email: data.appAccountEmail
          }),
          ToastMessageStatus.Success
        );
        if (data.appAccountEmail) {
          selectedResident?.setAppAccountEmail(data.appAccountEmail);
        }
        refreshAllData(true);
      }
    },
    onReject: () => {
      toastMessageStore.displayToastMessage(
        t("residentadmin:labels.createAccountFailureToast", {
          residentName: selectedResident?.formattedResidentName,
          email: selectedResident?.enteredEmail
        }),
        ToastMessageStatus.Danger
      );
    }
  });

  const removeAccount = useAsync({
    deferFn: (args) => residentAdminDomainStore.removeAccount(args[0]),
    onResolve: () => {
      toastMessageStore.displayToastMessage(
        t("residentadmin:labels.removeAccountSuccessToast", {
          residentName: selectedResident?.formattedResidentName
        }),
        ToastMessageStatus.Success
      );
      refreshAllData(true);

      setIsRemoveResidentConfirmationOpen(false);
    },
    onReject: () => {
      toastMessageStore.displayToastMessage(
        t("residentadmin:labels.removeAccountFailureToast", {
          residentName: selectedResident?.formattedResidentName,
          email: selectedResident?.enteredEmail
        }),
        ToastMessageStatus.Danger
      );
    }
  });

  const editEmail = useAsync({
    deferFn: (args) =>
      residentAdminDomainStore.changeEmail(
        selectedResident!.residentId,
        args[0]
      ),
    onResolve: (data: ResidentAccountDetail) => {
      setResidentAccountResponse(data);
      if (!data.error && !data.errorCode) {
        toastMessageStore.displayToastMessage(
          t("residentadmin:labels.editEmailSuccessToast", {
            residentName: selectedResident?.formattedResidentName,
            email: data.appAccountEmail
          }),
          ToastMessageStatus.Success
        );
        if (data.appAccountEmail) {
          selectedResident?.setAppAccountEmail(data.appAccountEmail);
        }
        // Reload the current page
        refreshAllData();
      }
    },
    onReject: () => {
      toastMessageStore.displayToastMessage(
        t("residentadmin:labels.editEmailFailureToast", {
          residentName: selectedResident?.formattedResidentName,
          email: selectedResident?.enteredEmail
        }),
        ToastMessageStatus.Danger
      );
    }
  });

  const resetPassword = useAsync({
    deferFn: (args) =>
      residentAdminDomainStore.resetPassword(selectedResident!.residentId),
    onResolve: (data: ResidentAccountDetail) => {
      if (data.tempPassword) {
        selectedResident?.setTempPassword(data.tempPassword);
        setIsResetPasswordSuccessModalOpen(true);
      }
    },
    onReject: () => {
      toastMessageStore.displayToastMessage(
        t("residentadmin:labels.resetPasswordFailureToast"),
        ToastMessageStatus.Danger
      );
    }
  });

  const showResidentModal = (resident: ResidentDetail) => {
    setSelectedResident(new ResidentModalDetail(resident, clearErrorMessage));
    setIsResidentDetailModalOpen(true);
  };

  const clearErrorMessage = () => {
    setResidentAccountResponse(undefined);
  };

  const closeResetPasswordSuccessModal = () => {
    selectedResident?.setTempPassword(undefined);
    setIsResetPasswordSuccessModalOpen(false);
  };

  return (
    <>
      <ResidentDetailModal
        resident={selectedResident}
        residentAccountDetails={residentAccountResponse}
        isLoading={
          createAccount.isPending ||
          editEmail.isPending ||
          resetPassword.isPending
        }
        isOpen={isResidentDetailModalOpen}
        namespace={namespace}
        createAccount={() => {
          clearErrorMessage();
          createAccount.run(selectedResident?.residentAccountCreateDetails);
        }}
        removeAccountStart={() => {
          // Close this modal
          setIsResidentDetailModalOpen(false);
          // Open the next - for user confirmation
          setIsRemoveResidentConfirmationOpen(true);
        }}
        editEmail={() => {
          clearErrorMessage();
          editEmail.run(selectedResident?.enteredEmail);
        }}
        clearSelectedResident={() => {
          if (createAccount.isPending) {
            createAccount.cancel();
          }
          if (editEmail.isPending) {
            editEmail.cancel();
          }
          setIsResidentDetailModalOpen(false);
          setResidentAccountResponse(undefined);
        }}
        clearErrorMessage={clearErrorMessage}
        resetPassword={() => {
          clearErrorMessage();
          resetPassword.run();
        }}
        isResetPasswordSuccessModalOpen={isResetPasswordSuccessModalOpen}
        closeResetPasswordSuccessModal={closeResetPasswordSuccessModal}
      />
      <RemoveAccountConfirmModal
        resident={selectedResident}
        isLoading={removeAccount.isPending}
        isOpen={isRemoveResidentConfirmationOpen}
        removeAccountConfirm={() => {
          clearErrorMessage();
          removeAccount.run(selectedResident?.residentAccountCreateDetails);
        }}
        clearSelectedResident={() => {
          if (removeAccount.isPending) {
            removeAccount.cancel();
          }
          setIsRemoveResidentConfirmationOpen(false);
          setResidentAccountResponse(undefined);
        }}
      />
      <ResidentAdminTemplate
        isResidentAdminListLoading={residentAdminUiStore.isListLoading}
        onSearch={(searchTerm) => {
          residentAdminUiStore.currentResidentAdminListSearchTerm = searchTerm;
        }}
        searchTerm={residentAdminUiStore.currentResidentAdminListSearchTerm}
        showResidentsModal={showResidentModal}
        residentAdminDomainStore={residentAdminDomainStore}
        residentAdminUiStore={residentAdminUiStore}
        namespace={namespace}
      />
    </>
  );
};

export default observer(ResidentAdminPage);
